import { makeObservable, observable } from 'mobx';
import { Ajax } from '@shared/Ajax';
import { alphaNumSort } from '@shared/utils/utils-sort';
import { Notify } from '@shared/ui-native/Notify';
import { store } from '../store/rootStore';
import { DateUtils } from '@shared/utils/DateUtils';
import { saveAs } from 'file-saver';
import { LStorage } from '@shared/Storage';
import { FilterStore } from '@pages/trucks/store/FilterStore';

type DerivedState = boolean | null;

function sumVolume(current: number | null, next: number | null | undefined) {
    if (next == null) {
        return current;
    }
    return (current ?? 0) + next;
}

function mergeDerivedState(current: DerivedState, nextValue: number | null | undefined, nextDerived: DerivedState | undefined) {
    if (nextValue == null) {
        return current;
    }
    if (current == null) {
        return nextDerived ?? false;
    }
    return current && (nextDerived ?? false);
}

function formatCsvNumber(value: number | null | undefined) {
    if (value == null) {
        return '';
    }
    return parseFloat(value.toFixed(2));
}

export interface ITripStat {
    material: IMaterial;
    volumes?: IVolumes;
    vehicleId?: number;
    date?: number;
    distance?: number;
    tripTime?: number;
    tripsCount?: number;
    loadAreaId?: number;
    unloadAreaId?: number;
    gpsLoadAreaId?: number;
    gpsUnloadAreaId?: number;
    taskId?: number;
    // set on frontend/fetch
    weekAndYear?: string;
    monthAndYear?: string;
    vehicleTitle?: string;
    loadAreaTitle?: string;
    unloadAreaTitle?: string;
    taskTitle?: string;
    m3Loose?: number | null;
    m3LooseDerived?: DerivedState;
    m3Solid?: number | null;
    m3SolidDerived?: DerivedState;
    tons?: number | null;
    tonsDerived?: DerivedState;
    pieces?: number | null;
    piecesDerived?: DerivedState;
}

export interface ITripStatsSum {
    totalM3Loose: number | null;
    totalM3LooseDerived: DerivedState;
    totalM3Solid: number | null;
    totalM3SolidDerived: DerivedState;
    totalTons: number | null;
    totalTonsDerived: DerivedState;
    totalPieces: number | null;
    totalPiecesDerived: DerivedState;
    totalDistance: number;
    totalTime: number;
    totalCount: number;
    items: ITripStat[];
}

export type GroupRange = 'day' | 'week' | 'month' | 'all';

export class StatsStore {
    @observable.shallow items: ITripStat[] = [];
    @observable isLoading: boolean = false;

    @observable groupBy: GroupRange = LStorage.getOrDefault('trucks_stats_group_by', 'all');

    activePromise = null;

    constructor(private tripsFilter: FilterStore) {
        makeObservable(this);
    }

    fetch = () => {
        if (this.activePromise) return this.activePromise;
        this.isLoading = true;

        const apiFilter = this.tripsFilter.toApiFilter();

        // Send the id filters as arrays (not comma-joined strings) so axios serializes them
        // as `key[]=1&key[]=2`, matching the controller's `@RequestParam("...[]")`. Joining
        // into `key=1,2` produced a param name the backend never bound, so every filter except
        // the date range was silently ignored. Send null when all options are active to keep
        // the query string small (mirrors TripsStore).
        const data = {
            ...apiFilter,
            projectId: kuura.getProjectId(),
            materialIds: this.tripsFilter.isAllIdsActiveForFilter('materials') ? null : apiFilter.materialIds,
            vehicleIds: this.tripsFilter.isAllIdsActiveForFilter('vehicles') ? null : apiFilter.vehicleIds,
            taskIds: this.tripsFilter.isAllIdsActiveForFilter('tasks') ? null : apiFilter.taskIds,
            loadAreasIds: this.tripsFilter.isAllIdsActiveForFilter('loadAreas') ? null : apiFilter.loadAreasIds,
            unloadAreasIds: this.tripsFilter.isAllIdsActiveForFilter('unloadAreas') ? null : apiFilter.unloadAreasIds,
            gpsLoadAreasIds: this.tripsFilter.isAllIdsActiveForFilter('gpsLoadAreas') ? null : apiFilter.gpsLoadAreasIds,
            gpsUnloadAreasIds: this.tripsFilter.isAllIdsActiveForFilter('gpsUnloadAreas') ? null : apiFilter.gpsUnloadAreasIds,
        };

        this.activePromise = Ajax({
            url: `trucks/stats/daily.json`,
            data,
        })
            .done((resp: any) => {
                const FIELD_EMPTY = -1;
                const FIELD_DELETED = -2;

                const areasIdsSet = new Set([FIELD_EMPTY, ...store.geofences.items.map(g => g.id)]);
                const taskIdsSet = new Set([FIELD_EMPTY, ...store.tasks.items.map(t => t.id)]);

                const areaNames = {
                    [FIELD_EMPTY]: kuura.getMessage('trucks.filter.no_area'),
                    [FIELD_DELETED]: kuura.getMessage('trucks.filter.deleted_area'),
                };

                const taskNames = {
                    [FIELD_EMPTY]: kuura.getMessage('trucks.filter.no_task'),
                    [FIELD_DELETED]: kuura.getMessage('trucks.filter.deleted_task'),
                };

                const vehicleNames = store.vehicles.items.reduce((obj, truck) => {
                    obj[truck.id] = truck.name;
                    return obj;
                }, {});
                store.geofences.items.forEach(a => (areaNames[a.id] = a.title));
                store.tasks.items.forEach(t => (taskNames[t.id] = t.name));

                resp.stats.reverse().forEach((s: ITripStat) => {
                    // normalize missing areaId, server returns (-1, 0, null)
                    s.loadAreaId = s.loadAreaId > 0 ? s.loadAreaId : FIELD_EMPTY;
                    s.unloadAreaId = s.unloadAreaId > 0 ? s.unloadAreaId : FIELD_EMPTY;
                    s.taskId = s.taskId > 0 ? s.taskId : FIELD_EMPTY;

                    // bad backend fix - areas are hard deleted but trip still keeps deleted area id, so merge all deleted area ids into single value
                    if (s.loadAreaId < 0) {
                        s.loadAreaId = FIELD_EMPTY;
                    } else {
                        if (!areasIdsSet.has(s.loadAreaId)) s.loadAreaId = FIELD_DELETED;
                    }
                    if (s.unloadAreaId < 0) {
                        s.unloadAreaId = FIELD_EMPTY;
                    } else {
                        if (!areasIdsSet.has(s.unloadAreaId)) s.unloadAreaId = FIELD_DELETED;
                    }
                    if (!taskIdsSet.has(s.taskId)) s.taskId = FIELD_DELETED;

                    // pre-calc dates
                    const m = moment(s.date);
                    s.weekAndYear = m.week() + ' ' + m.year();
                    s.monthAndYear = m.format('MMMM YYYY');

                    // set labels
                    s.vehicleTitle = vehicleNames[s.vehicleId];
                    s.loadAreaTitle = areaNames[s.loadAreaId];
                    s.unloadAreaTitle = areaNames[s.unloadAreaId];
                    s.taskTitle = taskNames[s.taskId];

                    const volumes = s.volumes || {};
                    s.m3Loose = volumes.m3Loose ?? null;
                    s.m3LooseDerived = volumes.m3Loose != null ? !!volumes.m3LooseDerived : null;
                    s.m3Solid = volumes.m3Solid ?? null;
                    s.m3SolidDerived = volumes.m3Solid != null ? !!volumes.m3SolidDerived : null;
                    s.tons = volumes.tons ?? null;
                    s.tonsDerived = volumes.tons != null ? !!volumes.tonsDerived : null;
                    s.pieces = volumes.pieces ?? null;
                    s.piecesDerived = volumes.pieces != null ? !!volumes.piecesDerived : null;
                });

                //this.tripsFilter.setFromStatsData(resp.stats);
                this.items = resp.stats;
            })
            .always(() => {
                this.isLoading = false;
                this.activePromise = null;
            })
            .fail(Notify.error);

        return this.activePromise;
    };

    groupByTimeAndMaterials(items: ITripStat[], groupBy: GroupRange): Record<string, ITripStatsSum> {
        const groupByTime = _.groupBy(items, s => {
            if (groupBy === 'day') return s.date;
            else if (groupBy === 'week') return s.weekAndYear;
            else if (groupBy === 'month') return s.monthAndYear;
            else if (groupBy === 'all') return 'all';
        });

        const summedGroups: Record<string, ITripStatsSum> = {};

        for (const groupKey in groupByTime) {
            summedGroups[groupKey] = this.sumByMaterial(groupByTime[groupKey]);
        }

        return summedGroups;
    }

    sumByMaterial(items: ITripStat[]): ITripStatsSum {
        const materialSums: {
            [key: string]: ITripStat;
        } = {};

        let totalM3Loose: number | null = null;
        let totalM3LooseDerived: DerivedState = null;
        let totalM3Solid: number | null = null;
        let totalM3SolidDerived: DerivedState = null;
        let totalTons: number | null = null;
        let totalTonsDerived: DerivedState = null;
        let totalPieces: number | null = null;
        let totalPiecesDerived: DerivedState = null;
        let totalDistance = 0;
        let totalTime = 0;
        let totalCount = 0;

        for (const s of items) {
            const key = String(s.material?.id ?? s.material?.uuid ?? s.material?.material);
            if (!materialSums.hasOwnProperty(key)) {
                materialSums[key] = {
                    ...s,
                    m3Loose: s.m3Loose ?? null,
                    m3Solid: s.m3Solid ?? null,
                    tons: s.tons ?? null,
                    pieces: s.pieces ?? null,
                    distance: s.distance || 0,
                    tripTime: s.tripTime || 0,
                    tripsCount: s.tripsCount || 0,
                };
            } else {
                materialSums[key].m3Loose = sumVolume(materialSums[key].m3Loose ?? null, s.m3Loose);
                materialSums[key].m3LooseDerived = mergeDerivedState(materialSums[key].m3LooseDerived ?? null, s.m3Loose, s.m3LooseDerived);
                materialSums[key].m3Solid = sumVolume(materialSums[key].m3Solid ?? null, s.m3Solid);
                materialSums[key].m3SolidDerived = mergeDerivedState(materialSums[key].m3SolidDerived ?? null, s.m3Solid, s.m3SolidDerived);
                materialSums[key].tons = sumVolume(materialSums[key].tons ?? null, s.tons);
                materialSums[key].tonsDerived = mergeDerivedState(materialSums[key].tonsDerived ?? null, s.tons, s.tonsDerived);
                materialSums[key].pieces = sumVolume(materialSums[key].pieces ?? null, s.pieces);
                materialSums[key].piecesDerived = mergeDerivedState(materialSums[key].piecesDerived ?? null, s.pieces, s.piecesDerived);

                if (s.distance) {
                    materialSums[key].distance += s.distance;
                }
                if (s.tripTime) {
                    materialSums[key].tripTime += s.tripTime;
                }
                if (s.tripsCount) {
                    materialSums[key].tripsCount += s.tripsCount;
                }
            }

            // calc totals
            totalM3Loose = sumVolume(totalM3Loose, s.m3Loose);
            totalM3LooseDerived = mergeDerivedState(totalM3LooseDerived, s.m3Loose, s.m3LooseDerived);
            totalM3Solid = sumVolume(totalM3Solid, s.m3Solid);
            totalM3SolidDerived = mergeDerivedState(totalM3SolidDerived, s.m3Solid, s.m3SolidDerived);
            totalTons = sumVolume(totalTons, s.tons);
            totalTonsDerived = mergeDerivedState(totalTonsDerived, s.tons, s.tonsDerived);
            totalPieces = sumVolume(totalPieces, s.pieces);
            totalPiecesDerived = mergeDerivedState(totalPiecesDerived, s.pieces, s.piecesDerived);

            if (s.distance) {
                totalDistance += s.distance;
            }
            if (s.tripTime) {
                totalTime += s.tripTime;
            }
            if (s.tripsCount) {
                totalCount += s.tripsCount;
            }
        }

        const sortedStats = Object.keys(materialSums)
            .map(key => materialSums[key])
            .sort((s1, s2) => alphaNumSort(s2.material, s1.material));

        return {
            totalM3Loose,
            totalM3LooseDerived,
            totalM3Solid,
            totalM3SolidDerived,
            totalTons,
            totalTonsDerived,
            totalPieces,
            totalPiecesDerived,
            totalDistance,
            totalTime,
            totalCount,
            items: sortedStats || [],
        };
    }

    export = () => {
        const items = this.items;
        const groups = this.groupByTimeAndMaterials(items, this.groupBy);
        let csv = `${this.groupBy !== 'all' ? this.groupBy + ',' : ''}material,m3 loose,m3 solid,tons,pieces,total time,trips,distance (km),average distance\n`;

        Object.entries(groups).forEach(([key, group]) => {
            group.items.forEach(row => {
                const date = this.groupBy === 'day' ? '"' + DateUtils.formatDate(parseInt(key)) + '"' : key;

                csv +=
                    (this.groupBy !== 'all' ? date + ',' : '') +
                    row.material.material.replace(',', ' ') +
                    ',' +
                    formatCsvNumber(row.m3Loose) +
                    ',' +
                    formatCsvNumber(row.m3Solid) +
                    ',' +
                    formatCsvNumber(row.tons) +
                    ',' +
                    formatCsvNumber(row.pieces) +
                    ',' +
                    DateUtils.secondsToDateTime(row.tripTime) +
                    ',' +
                    row.tripsCount +
                    ',' +
                    parseFloat((row.distance / 1000).toFixed(2)) +
                    ',' +
                    parseFloat((row.distance / row.tripsCount / 1000).toFixed(2)) +
                    '\n';
            });
        });

        const file = new Blob([csv], { type: 'text/csv;charset=utf-8' });
        saveAs(file, kuura.getProjectName() + '_truck_stats.csv');
    };

    setGroupBy = (val: GroupRange) => {
        this.groupBy = val;
        LStorage.setItem('trucks_stats_group_by', val);
    };
}
